import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo
} from 'react'
import './NewNiwari.css'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { theme } from '../../theme'
import {
  fetchSellers,
  fetchProductsBySellerCode,
  allocateWithProducts,
  fetchHinmeiByName,
  fetchShippersByHinmei
} from '../../services/Order'
import editIcon from '../../assets/edit.png'
import { getDraftById } from '../../services/Draft'
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  FormControlLabel,
  Grow,
  Popper,
  Popover,
  TextField
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import {
  createEditPayload,
  getSafeProductName
} from '../../services/distribution/transformPayload'
import {
  attachArrivalTimestamps,
  compareArrivalItems,
  filterArrivalItemsByReceivedDate,
  filterArrivalItemsByShippingDate,
  getArrivalDisplay,
  updateArrivalSelections
} from './arrivalInfo'
import {
  getInventoryStartDateDefault,
  resolveInventoryStartDate
} from '../../utils/inventoryDate'

const HinmeiCard = memo(
  ({ hinmei, isSelected, onToggle, onOriginClick, getOriginLabel }) => {
    const cardStyle = useMemo(
      () => ({
        background: isSelected ? '#e8f0fe' : '#ffffff',
        border: isSelected ? '1px solid #4285f4' : '1px solid #dadce0',
        borderRadius: '4px',
        padding: '2%',
        transition: 'all 0.2s ease',
        cursor: 'pointer'
      }),
      [isSelected]
    )

    const hasOrigins = hinmei.origins && hinmei.origins.length > 0

    const handleLabelClick = useCallback(
      (e) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle(hinmei)
      },
      [onToggle, hinmei]
    )

    const handleOriginSelectorClick = useCallback(
      (e) => {
        if (hasOrigins) onOriginClick(e, hinmei.code)
      },
      [hasOrigins, onOriginClick, hinmei.code]
    )

    const originLabel = getOriginLabel(hinmei)

    return (
      <div style={cardStyle}>
        <label style={labelStyle} onClick={handleLabelClick}>
          <div style={checkboxContainerStyle}>
            <FormControlLabel
              control={<Checkbox checked={isSelected} />}
              label={
                <span className='text-xl' style={labelTextStyle}>
                  {hinmei.name || hinmei.code}
                </span>
              }
            />
          </div>
          <div
            style={{ cursor: hasOrigins ? 'pointer' : 'default' }}
            onClick={handleOriginSelectorClick}
          >
            <div style={originSelectorStyle}>
              <span style={originLabelTextStyle}>{originLabel}</span>
              <span style={originArrowStyle}>▼</span>
            </div>
          </div>
        </label>
      </div>
    )
  }
)

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  cursor: 'pointer',
  width: '100%',
  minWidth: 0
}
const checkboxContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  minWidth: 0
}
const labelTextStyle = {
  fontWeight: '500',
  color: '#202124',
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word'
}
const originSelectorStyle = {
  padding: theme.spacing[3],
  borderRadius: theme.borderRadius.md,
  border: `1px solid ${theme.colors.gray[300]}`,
  background: theme.colors.background.primary,
  color: theme.colors.gray[900],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing[2],
  fontSize: theme.typography.fontSize.sm,
  transition: 'all 0.2s ease',
  boxShadow: theme.shadows.sm
}
const originLabelTextStyle = { fontSize: '20px', color: theme.colors.gray[700] }
const originArrowStyle = { fontSize: '20px', color: theme.colors.gray[500] }

const getSellerOptionLabel = (option) => {
  const name = option?.label || option?.name || ''
  return option?.id ? `${option.id} - ${name}` : name
}

// MUI captures an Autocomplete Popper's width when it opens.  That means a
// resize can leave the menu at its old width even though its input has changed
// size. Keep the Popper width in sync with its anchor while it is mounted.
const ResponsiveAutocompletePopper = React.forwardRef(
  function ResponsiveAutocompletePopper({ anchorEl, style, ...props }, ref) {
    const [anchorWidth, setAnchorWidth] = useState(null)

    useEffect(() => {
      if (!anchorEl) return undefined

      const updateWidth = () => setAnchorWidth(anchorEl.clientWidth)
      updateWidth()

      const resizeObserver = new ResizeObserver(updateWidth)
      resizeObserver.observe(anchorEl)

      return () => resizeObserver.disconnect()
    }, [anchorEl])

    return (
      <Popper
        {...props}
        ref={ref}
        anchorEl={anchorEl}
        style={{
          ...style,
          width: anchorWidth ?? style?.width,
          maxWidth: '100vw',
          boxSizing: 'border-box'
        }}
      />
    )
  }
)

const InventoryItem = memo(
  ({ item, hinmeiTab, isSelected, onToggle }) => {
    const display = getArrivalDisplay(item)

    const handleClick = useCallback(
      (e) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle(hinmeiTab, item.niukabango)
      },
      [onToggle, hinmeiTab, item.niukabango]
    )

    const handleMouseDown = useCallback((e) => {
      e.preventDefault()
    }, [])

    return (
      <div
        className={`inventory-item nn-arrival-item ${isSelected ? 'nn-arrival-item--selected' : ''}`}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        <div className='nn-arrival-item__row'>
          <span><strong className='nn-arrival-item__label'>出荷者:</strong> {display.shipper}</span>
          <span><strong className='nn-arrival-item__label'>数量:</strong> {display.quantity}</span>
          {display.saleType === '【買付】' ? (
            <span className='nn-arrival-item__sale-type'>【<span className='nn-arrival-item__sale-type-text'>買付</span>】</span>
          ) : (
            <span>{display.saleType}</span>
          )}
          <button
            type='button'
            className='nn-arrival-item__badge'
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            aria-pressed={isSelected}
          >
            {isSelected ? '選択済み' : '選択'}
          </button>
        </div>
        <div className='nn-arrival-item__row nn-arrival-item__details'>
          <span><strong className='nn-arrival-item__label'>区分:</strong> {display.dataCategory}</span>
          <span><strong className='nn-arrival-item__label'>出荷日:</strong> {display.shippingDate}</span>
          <span><strong className='nn-arrival-item__label'>受信日:</strong> {display.receivedDate}</span>
          <span><strong className='nn-arrival-item__label'>送り状No.:</strong> {display.waybill}</span>
          <span><strong className='nn-arrival-item__label'>貨車・トラNo.:</strong> {display.truckNumber}</span>
          <span><strong className='nn-arrival-item__label'>入荷No.:</strong> {display.arrivalNumber}</span>
          <span><strong className='nn-arrival-item__label'>荷口:</strong> {display.cargoNote}</span>
          <span><strong className='nn-arrival-item__label'>取置情報:</strong> {display.reserveInfo}</span>
        </div>
      </div>
    )
  }
)

const LeftInventoryPanel = memo(
  ({
    inventoryData,
    filteredInventoryData,
    shippingStartDate,
    shippingEndDate,
    setShippingStartDate,
    setShippingEndDate,
    shippingDateError,
    receivedStartDate,
    receivedEndDate,
    setReceivedStartDate,
    setReceivedEndDate,
    receivedDateError,
    activeHinmeiTab,
    setActiveHinmeiTab,
    selectedNyukaNumbers,
    onSelectAll,
    onDeselectAll,
    onToggleItem,
    onOpenResult,
    draftId,
    hinmeiProductNames
  }) => {
    const handleSelectAll = useCallback(
      (e) => {
        e.preventDefault()
        e.stopPropagation()
        onSelectAll()
      },
      [onSelectAll]
    )

    const handleDeselectAll = useCallback(
      (e) => {
        e.preventDefault()
        e.stopPropagation()
        onDeselectAll()
      },
      [onDeselectAll]
    )

    const canOpenResult = useMemo(
      () =>
        Object.values(selectedNyukaNumbers).some(
          (arr) => Array.isArray(arr) && arr.length > 0
        ) && !!draftId,
      [selectedNyukaNumbers, draftId]
    )

    const { totalInventoryCount, selectedInventoryCount } = useMemo(() => {
      return Object.entries(filteredInventoryData).reduce(
        (counts, [hinmeiCode, items]) => {
          const selectedItems = new Set(selectedNyukaNumbers[hinmeiCode] || [])
          const inventoryItems = Array.isArray(items) ? items : []

          counts.totalInventoryCount += inventoryItems.length
          counts.selectedInventoryCount += inventoryItems.filter((item) =>
            selectedItems.has(item.niukabango)
          ).length

          return counts
        },
        { totalInventoryCount: 0, selectedInventoryCount: 0 }
      )
    }, [filteredInventoryData, selectedNyukaNumbers])

    const hasDateFilter = !!(shippingStartDate || shippingEndDate || receivedStartDate || receivedEndDate)
    const hasDateError = shippingDateError || receivedDateError

    const isAllInventorySelected =
      totalInventoryCount > 0 &&
      selectedInventoryCount === totalInventoryCount
    const isAllInventoryDeselected = selectedInventoryCount === 0
    const visibleInventoryItems = useMemo(
      () => [...(filteredInventoryData[activeHinmeiTab] || [])].sort(compareArrivalItems),
      [filteredInventoryData, activeHinmeiTab]
    )

    return (
      <div style={leftPanelContainerStyle}>
        <div className='nn-shipping-filter'>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
            <div className='nn-shipping-filter__row'>
              <span className='nn-shipping-filter__title'>出荷日で絞り込み</span>
              <DatePicker
                label='開始日'
                value={shippingStartDate ? dayjs(shippingStartDate) : null}
                onChange={(value) => setShippingStartDate(
                  value?.isValid() ? value.format('YYYY-MM-DD') : ''
                )}
                format='YYYY年M月D日'
                slotProps={{
                  field: { clearable: true },
                  textField: { size: 'small', error: shippingDateError }
                }}
              />
              <span aria-hidden='true'>〜</span>
              <DatePicker
                label='終了日（任意）'
                value={shippingEndDate ? dayjs(shippingEndDate) : null}
                onChange={(value) => setShippingEndDate(
                  value?.isValid() ? value.format('YYYY-MM-DD') : ''
                )}
                format='YYYY年M月D日'
                slotProps={{
                  field: { clearable: true },
                  textField: { size: 'small', error: shippingDateError }
                }}
              />
            </div>
            <div className='nn-shipping-filter__row'>
              <span className='nn-shipping-filter__title'>受信日で絞り込み</span>
              <DatePicker
                label='開始日'
                value={receivedStartDate ? dayjs(receivedStartDate) : null}
                onChange={(value) => setReceivedStartDate(
                  value?.isValid() ? value.format('YYYY-MM-DD') : ''
                )}
                format='YYYY年M月D日'
                slotProps={{
                  field: { clearable: true },
                  textField: { size: 'small', error: receivedDateError }
                }}
              />
              <span aria-hidden='true'>〜</span>
              <DatePicker
                label='終了日（任意）'
                value={receivedEndDate ? dayjs(receivedEndDate) : null}
                onChange={(value) => setReceivedEndDate(
                  value?.isValid() ? value.format('YYYY-MM-DD') : ''
                )}
                format='YYYY年M月D日'
                slotProps={{
                  field: { clearable: true },
                  textField: { size: 'small', error: receivedDateError }
                }}
              />
              <button type='button' className='nn-btn nn-btn--secondary' onClick={() => {
                setShippingStartDate('')
                setShippingEndDate('')
                setReceivedStartDate('')
                setReceivedEndDate('')
              }} disabled={!hasDateFilter}>クリア</button>
            </div>
          </LocalizationProvider>
          <div className='nn-shipping-filter__hint' role={hasDateError ? 'alert' : undefined}>
            {shippingDateError
              ? '出荷日の終了日は開始日以降にしてください。'
              : receivedDateError
                ? '受信日の終了日は開始日以降にしてください。'
                : '終了日が未入力の場合、開始日以降のすべての出荷を表示します。'}
          </div>
        </div>
        <div className='nn-actionbar' style={actionBarStyle}>
          <button
            type='button'
            data-control
            className={`nn-btn ${
              isAllInventorySelected ? 'nn-btn--primary' : 'nn-btn--secondary'
            }`}
            onClick={handleSelectAll}
            onMouseDown={preventDefault}
            disabled={hasDateError || totalInventoryCount === 0}
          >
            <span>すべて選択</span>
          </button>
          <button
            type='button'
            data-control
            className={`nn-btn ${
              isAllInventoryDeselected
                ? 'nn-btn--primary'
                : 'nn-btn--secondary'
            }`}
            onClick={handleDeselectAll}
            onMouseDown={preventDefault}
            disabled={hasDateError || selectedInventoryCount === 0}
          >
            <span>すべて選択解除</span>
          </button>
          <button
            type='button'
            className={`nn-btn nn-btn--success ${canOpenResult ? '' : 'nn-btn--muted'}`}
            onClick={onOpenResult}
            onMouseDown={preventDefault}
            disabled={!canOpenResult || hasDateError}
          >
            <img
              src={editIcon}
              alt=''
              style={{ width: '16px', height: '16px', marginRight: '4px' }}
            />
            <span>結果を表示</span>
          </button>
        </div>

        <div style={tabsContainerStyle}>
          {Object.keys(inventoryData).map((code) => (
            <HinmeiTab
              key={code}
              code={code}
              isActive={activeHinmeiTab === code}
              title={
                hinmeiProductNames[code]
                  ? `${code} - ${hinmeiProductNames[code]}`
                  : code
              }
              onSelect={setActiveHinmeiTab}
            />
          ))}
        </div>

        {!activeHinmeiTab ? (
          <div
            className='fs-17'
            style={{ padding: '16px', textAlign: 'center', color: '#5f6368' }}
          >
            データがありません
          </div>
        ) : (
          <div style={inventoryScrollStyle}>
            {visibleInventoryItems.length === 0 && (
              <div className='nn-shipping-filter__empty'>該当する出荷がありません</div>
            )}
            {visibleInventoryItems.map((item, index) => (
              <InventoryItem
                key={`${activeHinmeiTab}-${item.niukabango}-${index}`}
                item={item}
                hinmeiTab={activeHinmeiTab}
                isSelected={
                  !!selectedNyukaNumbers[activeHinmeiTab]?.includes(
                    item.niukabango
                  )
                }
                onToggle={onToggleItem}
              />
            ))}
          </div>
        )}
      </div>
    )
  }
)

const leftPanelContainerStyle = {
  background: '#f8f9fa',
  border: '1px solid #dadce0',
  borderRadius: '4px',
  padding: '16px',
  position: 'relative',
  pointerEvents: 'auto',
  zIndex: 10
}
const actionBarStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '6px',
  flexWrap: 'wrap',
  marginBottom: '12px',
  position: 'relative',
  zIndex: 1000,
  pointerEvents: 'auto'
}
const tabsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: '12px',
  position: 'relative',
  zIndex: 9
}
const inventoryScrollStyle = {
  maxHeight: 'min(58vh, 520px)',
  overflowY: 'auto',
  overflowX: 'auto',
  pointerEvents: 'auto',
  position: 'relative',
  zIndex: 35
}
const preventDefault = (e) => {
  e.preventDefault()
  e.stopPropagation()
}

const HinmeiTab = memo(({ code, isActive, title, onSelect }) => {
  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault()
      onSelect(code)
    },
    [onSelect, code]
  )

  const handleClick = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      onSelect(code)
    },
    [onSelect, code]
  )

  return (
    <button
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      title={title}
      className={`nn-btn nn-tab ${isActive ? 'nn-tab--active' : ''}`}
      style={{
        width: 'auto',
        flex: '0 1 auto',
        minWidth: 0,
        maxWidth: '100%',
        whiteSpace: 'normal',
        overflowWrap: 'anywhere'
      }}
    >
      {title}
    </button>
  )
})

const RightNiwariPanel = memo(({ selectedNyukaNumbers, inventoryData }) => {
  const { selectedItems, customerList } = useMemo(() => {
    const items = []
    Object.entries(selectedNyukaNumbers).forEach(
      ([hinmeiCode, niukabangos]) => {
        niukabangos.forEach((niukabango) => {
          const niukaItem = inventoryData[hinmeiCode]?.find(
            (i) => i.niukabango === niukabango
          )
          if (niukaItem?.items?.[0]) {
            items.push({ hinmeiCode, niukabango, ...niukaItem.items[0] })
          }
        })
      }
    )
    const customers = new Set()
    items.forEach((item) => {
      ;(item.distribution_result || []).forEach((dist) => {
        if (dist.customer_code) customers.add(dist.customer_code)
      })
    })

    const compareText = (a, b) =>
      String(a ?? '').localeCompare(String(b ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })

    const sortedItems = [...items].sort((a, b) => {
      const productCmp = compareText(a?.hinmeiCode, b?.hinmeiCode)
      if (productCmp !== 0) return productCmp

      const nyukaCmp = compareText(a?.niukabango, b?.niukabango)
      if (nyukaCmp !== 0) return nyukaCmp

      const nyukaIndexA = Number(a?.nyuka_index)
      const nyukaIndexB = Number(b?.nyuka_index)
      const hasNyukaIndexA = Number.isFinite(nyukaIndexA)
      const hasNyukaIndexB = Number.isFinite(nyukaIndexB)
      if (hasNyukaIndexA && hasNyukaIndexB && nyukaIndexA !== nyukaIndexB) {
        return nyukaIndexA - nyukaIndexB
      }

      return 0
    })

    return { selectedItems: sortedItems, customerList: Array.from(customers) }
  }, [selectedNyukaNumbers, inventoryData])

  return (
    <div style={rightPanelContainerStyle}>
      <div style={rightPanelTitleStyle}>仮荷割（手修正ができる）</div>

      {selectedItems.length === 0 ? (
        <div style={{ opacity: 0.7, color: '#666' }}>
          入荷番号を選択して配分結果を表示します
        </div>
      ) : (
        <div style={rightPanelScrollStyle} className='custom-scrollbar'>
          <table className='nn-table'>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #e8eaed'
                }}
              >
                <th style={thStyle('200px', true)}>在庫アイテム</th>
                <th style={thStyle('120px', true)}>入荷番号</th>
                <th style={thStyle('100px', true)}>等級</th>
                <th style={thStyle('100px', true)}>サイズ</th>
                <th style={thStyle('120px', true)}>残数量</th>
                {customerList.map((code, i) => (
                  <th
                    key={code}
                    style={thStyle('120px', i < customerList.length - 1)}
                  >
                    {code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedItems.map((item, index) => (
                <AllocationRow
                  key={index}
                  item={item}
                  customerList={customerList}
                />
              ))}
            </tbody>
          </table>

          <AllocationSummary
            selectedItems={selectedItems}
            customerList={customerList}
          />
        </div>
      )}
    </div>
  )
})

const rightPanelContainerStyle = {
  background: '#ffffff',
  color: '#202124',
  border: '1px solid #dadce0',
  borderRadius: '4px',
  padding: '16px',
  minHeight: '300px',
  position: 'relative'
}
const rightPanelTitleStyle = {
  fontSize: 17,
  fontWeight: 700,
  marginBottom: 12,
  color: '#0f4c64'
}
const rightPanelScrollStyle = {
  maxHeight: '400px',
  overflowX: 'auto',
  overflowY: 'auto',
  border: '1px solid #e8eaed',
  borderRadius: '4px',
  backgroundColor: '#ffffff',
  boxShadow: '0 1px 3px rgba(60,64,67,.08), 0 4px 8px 3px rgba(60,64,67,.06)',
  scrollbarWidth: 'thin',
  scrollbarColor: '#dadce0 #f8f9fa',
  width: '100%',
  maxWidth: '100%'
}

const thStyle = (minWidth, hasBorder) => ({
  padding: '16px 24px',
  textAlign: 'center',
  fontWeight: '500',
  fontSize: '13px',
  color: '#3c4043',
  borderRight: hasBorder ? '1px solid #e8eaed' : 'none',
  minWidth,
  letterSpacing: '0.25px'
})

const AllocationRow = memo(({ item, customerList }) => {
  const handleMouseEnter = useCallback((e) => {
    e.target.closest('tr').style.backgroundColor = '#f8f9fa'
  }, [])
  const handleMouseLeave = useCallback((e) => {
    e.target.closest('tr').style.backgroundColor = '#ffffff'
  }, [])

  return (
    <tr
      style={{
        borderBottom: '1px solid #e8eaed',
        backgroundColor: '#ffffff',
        transition: 'background-color 0.2s ease',
        cursor: 'pointer'
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <td style={tdStyle(true)}>{item.hinmeiCode}</td>
      <td style={tdCenterStyle(true)}>{item.niukabango}</td>
      <td style={tdCenterStyle(true)}>{item.grade || '-'}</td>
      <td style={tdCenterStyle(true)}>{item.size || '-'}</td>
      <td
        style={{ ...tdCenterStyle(true), color: '#3c4043', fontWeight: '500' }}
      >
        {item.remain_quantity ?? '-'}
      </td>
      {customerList.map((code, i) => {
        const dist = (item.distribution_result || []).find(
          (d) => d.customer_code === code
        )
        return (
          <CustomerCell
            key={code}
            distribution={dist}
            isLast={i === customerList.length - 1}
          />
        )
      })}
    </tr>
  )
})

const tdStyle = (hasBorder) => ({
  padding: '16px 24px',
  fontWeight: '400',
  color: '#3c4043',
  borderRight: hasBorder ? '1px solid #e8eaed' : 'none',
  fontSize: '13px'
})
const tdCenterStyle = (hasBorder) => ({
  padding: '16px 24px',
  textAlign: 'center',
  color: '#5f6368',
  borderRight: hasBorder ? '1px solid #e8eaed' : 'none',
  fontSize: '13px'
})

const CustomerCell = memo(({ distribution, isLast }) => {
  const handleFocus = useCallback((e) => {
    e.target.style.borderColor = '#1a73e8'
    e.target.style.boxShadow = '0 0 0 1px #1a73e8'
  }, [])
  const handleBlur = useCallback((e) => {
    e.target.style.borderColor = '#dadce0'
    e.target.style.boxShadow = 'none'
  }, [])

  return (
    <td
      style={{
        padding: '16px 24px',
        textAlign: 'center',
        borderRight: isLast ? 'none' : '1px solid #e8eaed',
        fontSize: '13px'
      }}
    >
      <input
        type='number'
        defaultValue={distribution ? distribution.delivery_quantity : ''}
        placeholder='0'
        style={customerInputStyle}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </td>
  )
})

const customerInputStyle = {
  width: '80px',
  padding: '6px 12px',
  border: '1px solid #dadce0',
  borderRadius: '4px',
  textAlign: 'center',
  fontSize: '13px',
  backgroundColor: '#ffffff',
  color: '#3c4043',
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
}

const AllocationSummary = memo(({ selectedItems, customerList }) => {
  const totalRemain = useMemo(
    () =>
      selectedItems.reduce((sum, item) => sum + (item.remain_quantity || 0), 0),
    [selectedItems]
  )

  return (
    <div style={summaryContainerStyle}>
      <div
        className='fs-14'
        style={{
          fontWeight: '500',
          marginBottom: '12px',
          color: '#3c4043',
          letterSpacing: '0.25px'
        }}
      >
        配分サマリー
      </div>
      <div
        style={{
          display: 'flex',
          gap: '24px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}
      >
        <SummaryItem
          value={selectedItems.length}
          label='アイテム数'
          color='#1a73e8'
        />
        <SummaryItem value={totalRemain} label='総残数量' color='#34a853' />
        <SummaryItem
          value={customerList.length}
          label='大手商社'
          color='#ea4335'
        />
      </div>
    </div>
  )
})

const summaryContainerStyle = {
  marginTop: '16px',
  padding: '16px',
  backgroundColor: '#f8f9fa',
  borderRadius: '4px',
  border: '1px solid #e8eaed',
  boxShadow: '0 1px 3px rgba(60,64,67,.08)'
}

const SummaryItem = memo(({ value, label, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div
      style={{ fontSize: '24px', fontWeight: '400', color, lineHeight: '1.2' }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: '12px',
        color: '#5f6368',
        marginTop: '4px',
        letterSpacing: '0.25px'
      }}
    >
      {label}
    </div>
  </div>
))

const NotificationList = memo(({ notifications, onRemove }) => {
  if (!notifications.length) return null
  return (
    <div style={notifContainerStyle}>
      {notifications.map((n) => (
        <NotificationItem key={n.id} notification={n} onRemove={onRemove} />
      ))}
    </div>
  )
})

const notifContainerStyle = {
  position: 'fixed',
  top: '20px',
  right: '20px',
  zIndex: 1000000,
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxWidth: '400px'
}

const NOTIF_STYLES = {
  error: {
    background: '#fce8e6',
    border: '#d93025',
    icon: '⚠️',
    color: '#d93025'
  },
  success: {
    background: '#e8f5e8',
    border: '#34a853',
    icon: '',
    color: '#34a853'
  },
  info: {
    background: '#e3f2fd',
    border: '#4285f4',
    icon: 'spinner',
    color: '#4285f4'
  }
}

const NotificationItem = memo(({ notification, onRemove }) => {
  const styles = NOTIF_STYLES[notification.type] || NOTIF_STYLES.info
  const handleRemove = useCallback(
    () => onRemove(notification.id),
    [onRemove, notification.id]
  )

  return (
    <div
      style={{
        ...notifItemBaseStyle,
        background: styles.background,
        border: `1px solid ${styles.border}`
      }}
    >
      {styles.icon === 'spinner' ? (
        <div
          style={{
            ...spinnerStyle,
            border: `2px solid ${styles.color}33`,
            borderTop: `2px solid ${styles.color}`
          }}
        />
      ) : (
        <div
          className='fs-17'
          style={{ color: styles.color, marginTop: '2px' }}
        >
          {styles.icon}
        </div>
      )}
      <div style={{ flex: 1 }}>{notification.message}</div>
      <button onClick={handleRemove} style={notifCloseBtn}>
        ×
      </button>
    </div>
  )
})

const notifItemBaseStyle = {
  borderRadius: '4px',
  padding: '12px 16px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  fontSize: '14px',
  color: '#202124',
  animation: 'slideIn 0.3s ease-out'
}
const spinnerStyle = {
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginTop: '2px'
}
const notifCloseBtn = {
  background: 'none',
  border: 'none',
  color: '#5f6368',
  cursor: 'pointer',
  fontSize: '18px',
  padding: '0',
  marginLeft: '8px',
  lineHeight: 1
}

const loadSessionState = () => {
  try {
    const raw = sessionStorage.getItem('niwari_wizard_state')
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        selectedSeller: parsed.selectedSeller ?? null,
        hinmeiSelected: parsed.hinmeiSelected ?? [],
        allocationScope: parsed.allocationScope ?? 'm',
        allocationDate: parsed.allocationDate ?? '',
        inventoryStartDate: resolveInventoryStartDate(parsed),
        shippingStartDate: parsed.shippingStartDate || '',
        shippingEndDate: parsed.shippingEndDate || '',
        receivedStartDate: parsed.receivedStartDate || '',
        receivedEndDate: parsed.receivedEndDate || '',
        isComputeProfit: parsed.isComputeProfit ?? false,
        selectedHinmeiCodes: new Set(parsed.selectedHinmeiCodes || []),
        selectedOriginCdsByHinmeiCode: parsed.selectedOriginCdsByHinmeiCode || {},
        selectedNyukaNumbers: parsed.selectedNyukaNumbers || {},
        activeHinmeiTab: parsed.activeHinmeiTab || null
      }
    }
  } catch (e) {
    console.error('Failed to parse niwari_wizard_state:', e)
  }
  return {
    selectedSeller: null,
    hinmeiSelected: [],
    allocationScope: 'm',
    allocationDate: '',
    inventoryStartDate: getInventoryStartDateDefault(),
    shippingStartDate: '',
    shippingEndDate: '',
    receivedStartDate: '',
    receivedEndDate: '',
    isComputeProfit: false,
    selectedHinmeiCodes: new Set(),
    selectedOriginCdsByHinmeiCode: {},
    selectedNyukaNumbers: {},
    activeHinmeiTab: null
  }
}

const getTodayDateString = () => {
  const d = new Date()
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const NewNiwari = ({ draftData, setActiveTab }) => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const wizardStep = parseInt(searchParams.get('step'), 10) || 1

  const setWizardStep = useCallback(
    (step) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('step', String(step))
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  const [savedState] = useState(() => loadSessionState())

  const [selectedSeller, setSelectedSeller] = useState(savedState.selectedSeller)
  const [hinmeiSelected, setHinmeiSelected] = useState(savedState.hinmeiSelected)
  const [hinmeiSearchResults, setHinmeiSearchResults] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loadingSearch, setLoadingSearch] = useState(false)
  const [draftId, setDraftId] = useState('')
  const [isComputeProfit, setIsComputeProfit] = useState(savedState.isComputeProfit)
  const popupWindowRef = useRef(null)
  const [hinmeiProductNames, setHinmeiProductNames] = useState({})
  const [selectedShipperCodes, setSelectedShipperCodes] = useState(new Set())
  const [availableShippers, setAvailableShippers] = useState(new Set())
  const [allocationScope, setAllocationScope] = useState(savedState.allocationScope)
  const [allocationDate, setAllocationDate] = useState(() => {
    if (savedState.allocationDate) return savedState.allocationDate
    const d = new Date()
    d.setDate(d.getDate() + 1)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  })
  const [inventoryStartDate, setInventoryStartDate] = useState(
    savedState.inventoryStartDate
  )

  const [availableHinmei, setAvailableHinmei] = useState([])
  const [selectedHinmeiCodes, setSelectedHinmeiCodes] = useState(savedState.selectedHinmeiCodes)
  const [selectedOriginCdsByHinmeiCode, setSelectedOriginCdsByHinmeiCode] =
    useState(savedState.selectedOriginCdsByHinmeiCode)
  const [originAnchorEl, setOriginAnchorEl] = useState(null)
  const [activeOriginHinmeiCode, setActiveOriginHinmeiCode] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [inventoryData, setInventoryData] = useState({})
  const [shippingStartDate, setShippingStartDate] = useState(savedState.shippingStartDate)
  const [shippingEndDate, setShippingEndDate] = useState(savedState.shippingEndDate)
  const [receivedStartDate, setReceivedStartDate] = useState(savedState.receivedStartDate)
  const [receivedEndDate, setReceivedEndDate] = useState(savedState.receivedEndDate)
  const shippingDateError = !!(shippingStartDate && shippingEndDate && shippingStartDate > shippingEndDate)
  const receivedDateError = !!(receivedStartDate && receivedEndDate && receivedStartDate > receivedEndDate)
  const hasDateFilter = !!(shippingStartDate || shippingEndDate || receivedStartDate || receivedEndDate)
  const filteredInventoryData = useMemo(
    () => Object.fromEntries(
      Object.entries(inventoryData).map(([code, items]) => [
        code,
        filterArrivalItemsByReceivedDate(
          filterArrivalItemsByShippingDate(items || [], shippingStartDate, shippingEndDate),
          receivedStartDate,
          receivedEndDate
        )
      ])
    ),
    [inventoryData, shippingStartDate, shippingEndDate, receivedStartDate, receivedEndDate]
  )
  const [inventoryReady, setInventoryReady] = useState(false)
  const [selectedNyukaNumbers, setSelectedNyukaNumbers] = useState(savedState.selectedNyukaNumbers)
  const [allocationResult, setAllocationResult] = useState(null)
  const [activeHinmeiTab, setActiveHinmeiTab] = useState(savedState.activeHinmeiTab)
  const [rightPanelVisible, setRightPanelVisible] = useState(false)
  const [sellers, setSellers] = useState([])
  const [loadingSellers, setLoadingSellers] = useState(true)
  const [sellerLoadError, setSellerLoadError] = useState(false)
  const sellerFetchStartedRef = useRef(false)
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const [loadingAllocation, setLoadingAllocation] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const ref = useRef(null)

  const candidatesFetchedRef = useRef(false)
  const allocationFetchedRef = useRef(false)
  const prevStepRef = useRef(wizardStep)
  const popupCheckIntervalRef = useRef(null)

  // Reset fetch flags on step change
  useEffect(() => {
    candidatesFetchedRef.current = false
    allocationFetchedRef.current = false
  }, [wizardStep])

  // Sync React states back to URL searchParams (only step param is allowed)
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('step', String(wizardStep))
        // Make sure no other params are in URL
        next.delete('sellerCd')
        next.delete('date')
        next.delete('scope')
        next.delete('productCodes')
        next.delete('computeProfit')
        return next
      },
      { replace: true }
    )
  }, [wizardStep, setSearchParams])

  // Sync all selections and states to sessionStorage
  useEffect(() => {
    const stateToSave = {
      selectedSeller,
      hinmeiSelected,
      allocationScope,
      allocationDate,
      inventoryStartDate,
      shippingStartDate,
      shippingEndDate,
      receivedStartDate,
      receivedEndDate,
      isComputeProfit,
      selectedHinmeiCodes: Array.from(selectedHinmeiCodes),
      selectedOriginCdsByHinmeiCode,
      selectedNyukaNumbers,
      activeHinmeiTab
    }
    sessionStorage.setItem('niwari_wizard_state', JSON.stringify(stateToSave))
  }, [
    selectedSeller,
    hinmeiSelected,
    allocationScope,
    allocationDate,
    inventoryStartDate,
    shippingStartDate,
    shippingEndDate,
    receivedStartDate,
    receivedEndDate,
    isComputeProfit,
    selectedHinmeiCodes,
    selectedOriginCdsByHinmeiCode,
    selectedNyukaNumbers,
    activeHinmeiTab
  ])

  // Cleanup sessionStorage and intervals when unmounting (leaving the wizard)
  useEffect(() => {
    return () => {
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current)
      }
      const currentActiveTab = new URLSearchParams(window.location.search).get('activeTab')
      if (currentActiveTab !== 'new') {
        sessionStorage.removeItem('niwari_wizard_state')
      }
    }
  }, [])

  // Step transition cleanups: clears/resets states when navigating back
  useEffect(() => {
    if (wizardStep < prevStepRef.current) {
      if (wizardStep === 2) {
        // Going back to Step 2: clear Step 3 state and empty availableHinmei to trigger candidates reload
        setInventoryData({})
        setSelectedNyukaNumbers({})
        setActiveHinmeiTab(null)
        setAvailableHinmei([])
      } else if (wizardStep === 1) {
        // Going back to Step 1: clear Step 2 and Step 3 state
        setInventoryData({})
        setSelectedNyukaNumbers({})
        setActiveHinmeiTab(null)
        setAvailableHinmei([])
      }
    }
    prevStepRef.current = wizardStep
  }, [wizardStep])

  // Safeguards
  useEffect(() => {
    if (wizardStep === 2 && (!selectedSeller || !inventoryStartDate)) {
      setWizardStep(1)
    } else if (wizardStep === 3 && (!selectedSeller || selectedHinmeiCodes.size === 0)) {
      setWizardStep(1)
    }
  }, [wizardStep, selectedSeller, selectedHinmeiCodes, inventoryStartDate, setWizardStep])

  const addNotification = useCallback((type, message, persistent = false) => {
    const id = Date.now() + Math.random()
    setNotifications((prev) => [...prev, { id, type, message, persistent }])
    if (!persistent) {
      setTimeout(
        () => setNotifications((prev) => prev.filter((n) => n.id !== id)),
        5000
      )
    }
    return id
  }, [])

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id))
  }, [])

  const getOriginLabelForHinmei = useCallback(
    (hinmei) => {
      const selected = selectedOriginCdsByHinmeiCode[hinmei.code] || []
      const origins = hinmei.origins || []
      if (!origins.length) return '産地なし'
      if (!selected.length) return '未選択'
      const selectedNames = origins
        .filter((o) => selected.includes(o.id))
        .map((o) => o.name)
      if (!selectedNames.length) return '未選択'
      if (selectedNames.length === 1) return selectedNames[0]
      if (selectedNames.length === origins.length) return 'すべて選択中'
      return `${selectedNames.length}つ選択中`
    },
    [selectedOriginCdsByHinmeiCode]
  )

  const buildSelectedProductsForDraft = useCallback(() => {
    const byCode = new Map(availableHinmei.map((h) => [h.code, h]))
    const rows = []
    Array.from(selectedHinmeiCodes).forEach((code) => {
      const hinmei = byCode.get(code)
      if (!hinmei) return
      const selectedOriginCds = selectedOriginCdsByHinmeiCode[code] || []
      if (!selectedOriginCds.length) return
      selectedOriginCds.forEach((originCd) => {
        const origin = (hinmei.origins || []).find(
          (o) => String(o.id) === String(originCd)
        )
        if (!origin) return
        rows.push({
          seller_cd: hinmei.seller_cd || selectedSeller?.id || '',
          meisei_hinmei_cd: code,
          product_name: hinmei.name || '',
          place_of_origin_name: origin.name || '',
          place_of_origin_cd: String(origin.cd ?? '').trim()
        })
      })
    })
    return rows
  }, [
    availableHinmei,
    selectedHinmeiCodes,
    selectedOriginCdsByHinmeiCode,
    selectedSeller
  ])

  const getHinmeiByCode = useCallback(
    (code) => availableHinmei.find((h) => String(h.code) === String(code)),
    [availableHinmei]
  )

  const isOriginPopoverOpen = Boolean(originAnchorEl)

  const handleOriginClick = useCallback((event, hinmeiCode) => {
    event.preventDefault()
    event.stopPropagation()
    setOriginAnchorEl(event.currentTarget)
    setActiveOriginHinmeiCode(hinmeiCode)
  }, [])

  const handleOriginClose = useCallback(() => {
    setOriginAnchorEl(null)
    setActiveOriginHinmeiCode(null)
  }, [])

  const toggleHinmeiSelection = useCallback((hinmei) => {
    setSelectedHinmeiCodes((prev) => {
      const next = new Set(prev)
      const isSelected = next.has(hinmei.code)
      if (isSelected) {
        next.delete(hinmei.code)
        setSelectedOriginCdsByHinmeiCode((prevOrigins) => ({
          ...prevOrigins,
          [hinmei.code]: []
        }))
      } else {
        next.add(hinmei.code)
        const all = (hinmei.origins || []).map((o) => o.id)
        setSelectedOriginCdsByHinmeiCode((prevOrigins) => {
          const cur = Array.isArray(prevOrigins[hinmei.code])
            ? prevOrigins[hinmei.code]
            : []
          if (cur.length > 0) return prevOrigins
          return { ...prevOrigins, [hinmei.code]: all }
        })
      }
      return next
    })
  }, [])

  const removeHinmeiCode = useCallback((code) => {
    setHinmeiSelected((prev) => prev.filter((c) => c.code !== code))
  }, [])

  const handleToggleInventoryItem = useCallback((hinmeiTab, niukabango) => {
    setSelectedNyukaNumbers((prev) => {
      const currentSelections = prev[hinmeiTab] || []
      const isSelected = currentSelections.includes(niukabango)
      const newSelections = isSelected
        ? currentSelections.filter((num) => num !== niukabango)
        : [...currentSelections, niukabango]
      if (newSelections.length === 0) setAllocationResult(null)
      return { ...prev, [hinmeiTab]: newSelections }
    })
  }, [])

  const handleSelectAllInventory = useCallback(() => {
    const tabs = Object.keys(filteredInventoryData)
    if (!tabs.length) {
      addNotification('info', '品目タブが選択されていません')
      return
    }
    if (!activeHinmeiTab) setActiveHinmeiTab(tabs[0])
    setSelectedNyukaNumbers((prev) => updateArrivalSelections(
      prev, filteredInventoryData, true, hasDateFilter
    ))
  }, [filteredInventoryData, activeHinmeiTab, addNotification, hasDateFilter])

  const handleDeselectAllInventory = useCallback(() => {
    const tabs = Object.keys(filteredInventoryData)
    if (!tabs.length) {
      addNotification('info', '品目タブが選択されていません')
      return
    }
    if (!activeHinmeiTab) setActiveHinmeiTab(tabs[0])
    setSelectedNyukaNumbers((prev) => updateArrivalSelections(
      prev, filteredInventoryData, false, hasDateFilter
    ))
    setAllocationResult(null)
  }, [filteredInventoryData, activeHinmeiTab, addNotification, hasDateFilter])

  const handleSetAllocationScope = useCallback(
    (val) => setAllocationScope(val),
    []
  )
  const handleSetSelectedSeller = useCallback(
    (_, value) => setSelectedSeller(value),
    []
  )

  const handleSearchQueryChange = useCallback((e) => {
    const keyword = e.target.value
    setSearchQuery(keyword)
    setAnchorEl(keyword.trim() ? ref.current : null)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    setAnchorEl(null)
  }, [])

  const handleSearchClickAway = useCallback(() => setAnchorEl(null), [])

  const sellerOptions = useMemo(
    () => sellers.map((seller) => ({ label: seller.name, id: seller.id })),
    [sellers]
  )

  useEffect(() => {
    const query = searchQuery.trim()
    if (!query.length) {
      setHinmeiSearchResults([])
      setLoadingSearch(false)
      return
    }
    setLoadingSearch(true)
    fetchHinmeiByName(query, 20)
      .then((results) => {
        setHinmeiSearchResults(
          results.map((item) => ({
            code: item.meisei_hinmei_cd || item.code || '',
            name: item.product_name || item.PRODUCT_NAME || '',
            shipper_cd: item.shipper_cd
          }))
        )
      })
      .catch(() => setHinmeiSearchResults([]))
      .finally(() => setLoadingSearch(false))
  }, [searchQuery])

  useEffect(() => {
    if (sellerFetchStartedRef.current) return
    sellerFetchStartedRef.current = true
    fetchSellers()
      .then((data) => {
        const normalized = Array.isArray(data)
          ? data
              .map((s) => ({
                id: s?.id ?? s?.seller_cd ?? '',
                name: s?.name ?? s?.seller_name ?? ''
              }))
              .filter((s) => String(s.id).length > 0)
          : []
        setSellers(normalized)
        setSelectedSeller((prev) => {
          if (!prev) return prev
          const matched = normalized.find((s) => String(s.id) === String(prev.id))
          return matched ? { id: matched.id, label: matched.name } : prev
        })
      })
      .catch((err) => {
        console.error('Error fetching sellers:', err)
        setSellerLoadError(true)
      })
      .finally(() => setLoadingSellers(false))
  }, [])

  useEffect(() => {
    setAllocationResult(draftData?.allocation_result ?? null)
  }, [draftData])

  useEffect(() => {
    const keys = Object.keys(inventoryData)
    if (keys.length > 0) {
      setActiveHinmeiTab((prev) => {
        if (prev && keys.includes(prev)) return prev
        return keys[0]
      })
    } else {
      setActiveHinmeiTab(null)
    }
  }, [inventoryData])

  useEffect(() => {
    if (
      wizardStep === 3 &&
      Object.keys(inventoryData).length > 0 &&
      !activeHinmeiTab
    ) {
      setActiveHinmeiTab(Object.keys(inventoryData)[0])
    }
  }, [wizardStep, inventoryData, activeHinmeiTab])

  useEffect(() => {
    if (
      wizardStep === 3 &&
      Object.keys(inventoryData).length > 0 &&
      Object.keys(selectedNyukaNumbers).length === 0
    ) {
      const allDeselected = {}
      Object.keys(inventoryData).forEach((hinmeiCode) => {
        allDeselected[hinmeiCode] = []
      })
      setSelectedNyukaNumbers(allDeselected)
    }
  }, [wizardStep, inventoryData, selectedNyukaNumbers])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown]')) setDropdownOpen({})
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (wizardStep !== 1) return
    try {
      const key = 'app_notifications'
      const path = window.location ? window.location.pathname : '/'
      const raw = localStorage.getItem(key)
      const all = raw ? JSON.parse(raw) : {}
      const items = Array.isArray(all[path]) ? all[path] : []
      const title = t('schedule.messages.targetSelectionTitle', '対象選択画面')
      const message = t(
        'schedule.wizard.step1.description',
        'セリ人指定（過去データから品目をセレクト） or 品目コード指定 or 出荷者コード'
      )
      if (!items.some((it) => it.title === title && it.message === message)) {
        const note = { title, message, time: new Date().toLocaleString() }
        all[path] = [note, ...items]
        localStorage.setItem(key, JSON.stringify(all))
      }
      try {
        window.dispatchEvent(new Event('appNotificationsUpdated'))
      } catch (_) {}
    } catch (e) {
      console.error('Failed to add screen notification', e)
    }
  }, [wizardStep, t])

  const fetchShippersForHinmeiCodes = useCallback(async (newHinmeiCodes) => {
    try {
      const shippers = await fetchShippersByHinmei(newHinmeiCodes)
      setAvailableShippers((prev) => {
        const s = new Set(prev)
        shippers.forEach((sh) => s.add(sh.shipper_cd))
        return s
      })
      setSelectedShipperCodes((prev) => {
        const s = new Set(prev)
        shippers.forEach((sh) => s.add(sh.shipper_cd))
        return s
      })
    } catch (err) {
      console.error('Error fetching shippers:', err)
    }
  }, [])

  useEffect(() => {
    if (hinmeiSelected.length > 0) {
      fetchShippersForHinmeiCodes(hinmeiSelected).catch(console.error)
    } else {
      setSelectedShipperCodes(new Set())
      setAvailableShippers(new Set())
    }
  }, [hinmeiSelected, fetchShippersForHinmeiCodes])

  const fetchCandidates = useCallback(
    async (preserveSelections = false) => {
      if (!selectedSeller) {
        addNotification('error', 'セラーを選択してください')
        setAvailableHinmei([])
        if (!preserveSelections) {
          setSelectedHinmeiCodes(new Set())
          setSelectedOriginCdsByHinmeiCode({})
        }
        return false
      }
      if (!inventoryStartDate) {
        addNotification('error', '在庫取得開始日を選択してください')
        return false
      }
      if (inventoryStartDate > allocationDate) {
        addNotification('error', '在庫取得開始日は配達日以前の日付を選択してください')
        return false
      }

      setLoadingCandidates(true)
      const loadingId = addNotification('info', '候補データを取得中...', true)

      const normalizeProductsResponse = (data) => {
        if (!data) return []
        if (Array.isArray(data)) return data
        if (Array.isArray(data.products)) return data.products
        return []
      }

      const buildHinmeiCandidates = (products) => {
        const grouped = new Map()
        ;(products || []).forEach((item) => {
          const code = item.meisei_hinmei_cd
          if (!code) return
          const name = item.PRODUCT_NAME || item.product_name || ''
          const sellerCdVal = item.seller_cd || ''
          const originCd = item.place_of_origin_cd || ''
          const originName = item.place_of_origin_name || ''
          if (!grouped.has(code)) {
            grouped.set(code, { code, name, seller_cd: sellerCdVal, origins: [] })
          } else {
            const entry = grouped.get(code)
            if (!entry.seller_cd && sellerCdVal) entry.seller_cd = sellerCdVal
          }
          if (originCd || originName) {
            const entry = grouped.get(code)
            const exists = entry.origins.some(
              (o) =>
                String(o.cd) === String(originCd) &&
                String(o.name) === String(originName)
            )
            if (!exists) {
              const stableId = `${originCd || ''}_${originName || ''}`
              entry.origins.push({ cd: originCd, name: originName, id: stableId })
            }
          }
        })
        return Array.from(grouped.values())
      }

      try {
        const data = await fetchProductsBySellerCode({
          seller_cd: selectedSeller.id,
          delivery_date: allocationDate,
          inventory_start_date: inventoryStartDate,
          days_query: allocationScope,
          product_codes: hinmeiSelected.map((c) => c.code),
          compute_profit: isComputeProfit
        })
        const candidates = buildHinmeiCandidates(
          normalizeProductsResponse(data)
        )
        setAvailableHinmei(candidates)
        if (!preserveSelections) {
          setSelectedHinmeiCodes(new Set())
          setSelectedOriginCdsByHinmeiCode({})
        } else {
          // Smart Selection Filter: Filter out invalid selections that no longer exist in the new candidates list
          setSelectedHinmeiCodes((prevCodes) => {
            const nextCodes = new Set()
            prevCodes.forEach((code) => {
              const candidate = candidates.find((h) => String(h.code) === String(code))
              if (candidate) {
                nextCodes.add(code)
              }
            })
            return nextCodes
          })

          setSelectedOriginCdsByHinmeiCode((prevOrigins) => {
            const nextOrigins = {}
            Object.entries(prevOrigins).forEach(([code, origins]) => {
              const candidate = candidates.find((h) => String(h.code) === String(code))
              if (candidate) {
                const validOrigins = (origins || []).filter((origId) =>
                  candidate.origins.some((o) => String(o.id) === String(origId))
                )
                if (validOrigins.length > 0) {
                  nextOrigins[code] = validOrigins
                }
              }
            })
            return nextOrigins
          })
        }
      } catch (err) {
        console.error('Error fetching products:', err)
        addNotification('error', '候補データの取得に失敗しました')
        setAvailableHinmei([])
        return false
      } finally {
        removeNotification(loadingId)
        setLoadingCandidates(false)
      }
      return true
    },
    [
      selectedSeller,
      allocationDate,
      inventoryStartDate,
      allocationScope,
      hinmeiSelected,
      isComputeProfit,
      addNotification,
      removeNotification
    ]
  )

  const handleGetCandidates = useCallback(async () => {
    if (await fetchCandidates(false)) setWizardStep(2)
  }, [fetchCandidates, setWizardStep])

  const fetchAllocationData = useCallback(
    async (preserveSelections = false) => {
      if (loadingAllocation) return false
      if (!inventoryStartDate || inventoryStartDate > allocationDate) {
        addNotification('error', '在庫取得開始日は配達日以前の日付を選択してください')
        return false
      }
      setLoadingAllocation(true)
      setNotifications([])
      const loadingId = addNotification('info', '荷割データを取得中…', true)

      try {
        const selectedProductsForDraft = buildSelectedProductsForDraft()
        const list_hic = Array.from(selectedHinmeiCodes)
        const productsWithValidOrigins = new Set(
          selectedProductsForDraft.map((item) => String(item.meisei_hinmei_cd))
        )

        const missingOrigin = list_hic.find((code) => {
          const hinmei = availableHinmei.find(
            (h) => String(h.code) === String(code)
          )
          if (!hinmei || !(hinmei.origins || []).length) return false
          return !productsWithValidOrigins.has(String(code))
        })
        if (missingOrigin) {
          addNotification('error', '産地（コード）を1つ以上選択してください')
          return false
        }
        if (!selectedProductsForDraft.length) {
          addNotification('error', '産地（コード）を選択した品目がありません')
          return false
        }

        const sellerCdForAllocate =
          selectedSeller?.id || selectedProductsForDraft[0]?.seller_cd || ''
        if (!sellerCdForAllocate) {
          addNotification('error', 'seller_cd が取得できません')
          return false
        }

        try {
          const result = await allocateWithProducts({
            seller_cd: sellerCdForAllocate,
            delivery_date: allocationDate,
            products: selectedProductsForDraft,
            meisei_code:
              list_hic[0] || selectedProductsForDraft[0]?.meisei_hinmei_cd || '',
            days_query: allocationScope,
            inventory_start_date: inventoryStartDate
          })
          const productList = result?.result?.data?.product_list
          if (!Array.isArray(productList) || productList.length === 0) {
            throw new Error('No inventory returned for the selected products')
          }

          const grouped = {}
          productList.forEach((p) => {
            const code =
              p.meisei_hinmei_cd ||
              p.list_niukabango?.[0]?.meisei_hinmei_cd ||
              p.list_niukabango?.[0]?.items?.[0]?.meisei_hinmei_cd ||
              ''
            const items = Array.isArray(p.list_niukabango)
              ? p.list_niukabango
              : []
            if (!code || items.length === 0) return
            grouped[code] = items
          })
          if (Object.keys(grouped).length === 0) {
            throw new Error('No inventory items returned for the selected products')
          }
          setDraftId(result.draft_id || '')

          const productNames = {}
          availableHinmei.forEach(({ code, name }) => {
            if (code && name) productNames[code] = name
          })
          productList.forEach((p) => {
            const productName = p.product_name || p.PRODUCT_NAME || ''
            if (p.meisei_hinmei_cd && productName)
              productNames[p.meisei_hinmei_cd] = productName
          })

          if (!preserveSelections) {
            setSelectedNyukaNumbers({})
          } else {
            // Smart Selection Filter: Filter out invalid nyuka numbers that no longer exist in the new inventory data
            setSelectedNyukaNumbers((prevNyukas) => {
              const nextNyukas = {}
              Object.entries(prevNyukas).forEach(([code, numbers]) => {
                const newItems = grouped[code]
                if (newItems) {
                  const validNumbers = (numbers || []).filter((num) =>
                    newItems.some((item) => String(item.niukabango) === String(num))
                  )
                  if (validNumbers.length > 0) {
                    nextNyukas[code] = validNumbers
                  }
                }
              })
              return nextNyukas
            })
          }
          setInventoryData(grouped)
          setHinmeiProductNames(productNames)
          if (Object.keys(grouped).length > 0) {
            const savedState = loadSessionState()
            if (preserveSelections && savedState.activeHinmeiTab && grouped[savedState.activeHinmeiTab]) {
              setActiveHinmeiTab(savedState.activeHinmeiTab)
            } else {
              setActiveHinmeiTab(Object.keys(grouped)[0])
            }
          }
          addNotification('success', '荷割データの取得が完了しました')
          return true
        } catch (err) {
          console.error('Error allocating:', err)
          addNotification('error', '指定された商品に一致する在庫がありません')
          setInventoryData({})
          return false
        }
      } finally {
        removeNotification(loadingId)
        setLoadingAllocation(false)
        setInventoryReady(true)
      }
    },
    [
      loadingAllocation,
      buildSelectedProductsForDraft,
      selectedHinmeiCodes,
      availableHinmei,
      selectedSeller,
      allocationDate,
      inventoryStartDate,
      allocationScope,
      addNotification,
      removeNotification
    ]
  )

  const handleProceedToAllocation = useCallback(async () => {
    if (await fetchAllocationData(false)) setWizardStep(3)
  }, [fetchAllocationData, setWizardStep])

  const handleOpenResult = useCallback(async () => {
    const hasSelection = Object.values(selectedNyukaNumbers).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    )
    if (!hasSelection)
      addNotification('info', '入荷番号が未選択のため、空の状態で開きます')

    const payloadKey = `niwariPopup:${draftId}`
    try {
      const draftDetail = await getDraftById(
        draftId,
        hasSelection ? Object.values(selectedNyukaNumbers).flat() : ['dummy']
      )
      const apiData = draftDetail.output_data
      let sellerCode = ''
      let customers = []
      let rawInventory = []
      try {
        const inputData =
          typeof draftDetail.input_data === 'string'
            ? JSON.parse(draftDetail.input_data)
            : draftDetail.input_data
        const dataSection = inputData?.data || {}
        if (Array.isArray(dataSection?.customers)) {
          customers = dataSection.customers
        }
        rawInventory =
          dataSection?.inventory ||
          inputData?.inventory ||
          inputData?.inventoryList ||
          []
        sellerCode =
          dataSection?.seller_code ||
          dataSection?.seller_cd ||
          inputData?.seller_code ||
          inputData?.seller_cd ||
          ''
      } catch (e) {
        sellerCode = ''
      }
      const orders = apiData?.result?.orders || []
      const productName = getSafeProductName(apiData.result.data)
      const saleMan = draftDetail.name
      const editPayload = createEditPayload(apiData, hinmeiProductNames)
      const payload = {
        ...editPayload,
        inventoryData: attachArrivalTimestamps(
          editPayload?.inventoryData,
          rawInventory
        ),
        selectedNyukaNumbers: { ...selectedNyukaNumbers },
        productName,
        saleMan,
        orders,
        customers,
        seller_code: sellerCode,
        createdAt: draftDetail.created_at
      }

      sessionStorage.removeItem(payloadKey)
      await new Promise((resolve) => setTimeout(resolve, 50))
      sessionStorage.setItem(payloadKey, JSON.stringify(payload))
      await new Promise((resolve) => setTimeout(resolve, 50))

      if (popupWindowRef.current && !popupWindowRef.current.closed) {
        popupWindowRef.current.close()
        popupWindowRef.current = null
        if (popupCheckIntervalRef.current) {
          clearInterval(popupCheckIntervalRef.current)
          popupCheckIntervalRef.current = null
        }
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      const params = new URLSearchParams({ payloadKey, draftId })
      const url = `${window.location.origin}/allocation/schedule/right-panel?${params.toString()}`
      const popupHeight = Math.floor(window.screen.height * 0.8)
      const popupWidth = Math.floor(window.screen.width * 0.9)
      const popup = window.open(
        url,
        `niwari_${draftId}`,
        `width=${popupWidth},height=${popupHeight},scrollbars=yes,resizable=yes,status=yes`
      )
      if (popup) {
        popupWindowRef.current = popup
        popup.focus()

        if (popupCheckIntervalRef.current) {
          clearInterval(popupCheckIntervalRef.current)
        }
        popupCheckIntervalRef.current = setInterval(() => {
          if (!popup || popup.closed) {
            clearInterval(popupCheckIntervalRef.current)
            popupCheckIntervalRef.current = null
            popupWindowRef.current = null
            // Automatically refresh allocations on Step 3 and keep selections
            fetchAllocationData(true)
          }
        }, 500)
      }
    } catch (e) {
      console.error('Failed to load draft data:', e)
      addNotification('error', 'データの取得に失敗しました')
    }
  }, [selectedNyukaNumbers, draftId, addNotification, hinmeiProductNames, fetchAllocationData])

  // Auto-fetch candidates for Step 2 and Step 3 on mount/reload
  useEffect(() => {
    if (
      (wizardStep === 2 || wizardStep === 3) &&
      availableHinmei.length === 0 &&
      selectedSeller &&
      !candidatesFetchedRef.current
    ) {
      candidatesFetchedRef.current = true
      fetchCandidates(true)
    }
  }, [wizardStep, availableHinmei.length, selectedSeller, inventoryStartDate, fetchCandidates])

  // Auto-fetch allocations for Step 3 on mount/reload (once candidates are loaded)
  useEffect(() => {
    if (
      wizardStep === 3 &&
      availableHinmei.length > 0 &&
      Object.keys(inventoryData).length === 0 &&
      !allocationFetchedRef.current
    ) {
      allocationFetchedRef.current = true
      fetchAllocationData(true).then((loaded) => {
        if (!loaded) setWizardStep(2)
      })
    }
  }, [wizardStep, availableHinmei.length, inventoryData, fetchAllocationData, setWizardStep])

  const handleSelectAllHinmei = useCallback(() => {
    setSelectedHinmeiCodes(new Set(availableHinmei.map((h) => h.code)))
    setSelectedOriginCdsByHinmeiCode((prev) => {
      const next = { ...prev }
      availableHinmei.forEach((h) => {
        next[h.code] = (h.origins || []).map((o) => o.id)
      })
      return next
    })
  }, [availableHinmei])

  const handleDeselectAllHinmei = useCallback(() => {
    setSelectedHinmeiCodes(new Set())
    setSelectedOriginCdsByHinmeiCode({})
  }, [])

  const handleOriginSelectAll = useCallback(
    (hinmei) => (e) => {
      e.preventDefault()
      e.stopPropagation()
      setSelectedOriginCdsByHinmeiCode((prev) => ({
        ...prev,
        [hinmei.code]: (hinmei.origins || []).map((o) => o.id)
      }))
      setSelectedHinmeiCodes((prev) => {
        const next = new Set(prev)
        next.add(hinmei.code)
        return next
      })
    },
    []
  )

  const handleOriginDeselect = useCallback(
    (hinmei) => (e) => {
      e.preventDefault()
      e.stopPropagation()
      setSelectedOriginCdsByHinmeiCode((prev) => ({
        ...prev,
        [hinmei.code]: []
      }))
      setSelectedHinmeiCodes((prev) => {
        const next = new Set(prev)
        next.delete(hinmei.code)
        return next
      })
    },
    []
  )

  const handleOriginToggle = useCallback(
    (hinmei, o) => (e) => {
      e.preventDefault()
      e.stopPropagation()
      setSelectedOriginCdsByHinmeiCode((prev) => {
        const current = prev[hinmei.code] || []
        const newOrigins = current.includes(o.id)
          ? current.filter((item) => item !== o.id)
          : [...current, o.id]
        setSelectedHinmeiCodes((prevCodes) => {
          const next = new Set(prevCodes)
          newOrigins.length === 0
            ? next.delete(hinmei.code)
            : next.add(hinmei.code)
          return next
        })
        return { ...prev, [hinmei.code]: newOrigins }
      })
    },
    []
  )

  const [, setDropdownOpen] = useState({})

  const activeOriginHinmei = useMemo(
    () => getHinmeiByCode(activeOriginHinmeiCode),
    [getHinmeiByCode, activeOriginHinmeiCode]
  )

  return (
    <>
      <NotificationList
        notifications={notifications}
        onRemove={removeNotification}
      />

      <div className='new-niwari-wizard'>
        <div className='page-schedule-layout' style={pageLayoutStyle}>
          <div
            className='new-niwari-wizard panel-card fs-17'
            style={wizardPanelStyle}
          >
            <div style={wizardHeaderStyle}>
              <div style={wizardNavigationStyle}>
                <div className='nn-step-header text-2xl' aria-live='polite'>
                  ステップ{wizardStep}/3
                </div>
                <button
                  type='button'
                  onClick={() => setActiveTab?.(null)}
                  className='nn-btn nn-btn--back nn-btn--lg text-2xl'
                >
                  ← 最初へ戻る
                </button>
                {wizardStep > 1 && (
                  <button
                    type='button'
                    onClick={() => setWizardStep(wizardStep - 1)}
                    className='nn-btn nn-btn--back nn-btn--lg text-2xl'
                    disabled={loadingAllocation}
                  >
                    ← ひとつ前へ戻る
                  </button>
                )}
              </div>
              {wizardStep === 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='ja'>
                    <DatePicker
                      value={inventoryStartDate ? dayjs(inventoryStartDate) : null}
                      onChange={(newValue) => {
                        setInventoryStartDate(
                          newValue?.isValid() ? newValue.format('YYYY-MM-DD') : ''
                        )
                      }}
                      format='YYYY年M月D日'
                      maxDate={dayjs(getTodayDateString())}
                      disabled={loadingCandidates}
                      slotProps={{
                        field: { clearable: true },
                        textField: { size: 'small', label: '在庫取得開始日' }
                      }}
                    />
                  </LocalizationProvider>
                  {['d', 'm', 'y'].map((val, i) => (
                    <label
                      key={val}
                      style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      <input
                        type='radio'
                        name='allocScope'
                        value={val}
                        checked={allocationScope === val}
                        disabled={loadingCandidates}
                        onChange={() => handleSetAllocationScope(val)}
                      />
                      <span className='text-2xl'>{['日', '月', '年'][i]}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {wizardStep === 1 && (
              <div style={{ padding: '8px' }}>
                <div
                  className='text-2xl'
                  style={{
                    fontWeight: '500',
                    marginBottom: '8px',
                    color: '#202124'
                  }}
                >
                  {t('schedule.wizard.step1.title', '対象選択画面')}
                </div>

                <div className='nn-gcp-grid'>
                  {/* Seller */}
                  <div className='nn-gcp-card'>
                    <div className='nn-gcp-card__header'>
                      <span className='nn-gcp-card__icon' aria-hidden='true'>
                        <svg viewBox='0 0 16 16' focusable='false'>
                          <path d='M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM2 14.25c0-2.34 2.69-4.25 6-4.25s6 1.91 6 4.25a.75.75 0 0 1-1.5 0c0-1.32-1.98-2.75-4.5-2.75S3.5 12.93 3.5 14.25a.75.75 0 0 1-1.5 0Z' />
                        </svg>
                      </span>
                      {t('schedule.wizard.step1.sellerSelection', 'せり人指定')}
                    </div>
                    <div className='nn-gcp-card__body'>
                      <div
                        className='nn-select-wrap'
                        aria-busy={loadingSellers ? 'true' : 'false'}
                      >
                        <Autocomplete
                          options={sellerOptions}
                          openOnFocus
                          PopperComponent={ResponsiveAutocompletePopper}
                          getOptionLabel={getSellerOptionLabel}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          loading={loadingSellers}
                          loadingText='せり人を読み込み中...'
                          noOptionsText={
                            sellerLoadError
                              ? 'せり人の取得に失敗しました'
                              : 'データがありません'
                          }
                          value={selectedSeller}
                          onChange={handleSetSelectedSeller}
                          renderOption={(props, option) => (
                            <li
                              {...props}
                              key={option.id}
                              className='text-2xl border-b-2 border-gray-400 px-2 py-4 cursor-pointer'
                            >
                              {getSellerOptionLabel(option)}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder='セリ人番号または名前を入力'
                              sx={{
                                '& .MuiInputBase-root': {
                                  height: 40,
                                  padding: '0 8px'
                                },
                                '& .MuiInputBase-input': {
                                  padding: 0,
                                  fontSize: 24
                                }
                              }}
                            />
                          )}
                          sx={{
                            width: '100%',
                            height: 40,
                            marginBottom: '16px !important'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className='nn-gcp-card'>
                    <div className='nn-gcp-card__header'>
                      <span className='nn-gcp-card__icon' aria-hidden='true'>
                        <svg viewBox='0 0 16 16' focusable='false'>
                          <path d='M11.5 10.5h-.79l-.28-.27a6 6 0 1 0-.71.71l.27.28v.79l3 3a.75.75 0 0 0 1.06-1.06l-3-3Zm-5.5 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Z' />
                        </svg>
                      </span>
                      {t('schedule.wizard.step1.productSelection', '品目指定')}
                    </div>
                    <div
                      className='nn-gcp-card__body'
                      style={{ paddingBottom: 12 }}
                    >
                      <div style={{ marginBottom: theme.spacing[4] }}>
                        <ClickAwayListener onClickAway={handleSearchClickAway}>
                          <Box ref={ref} className='relative'>
                            <input
                              type='text'
                              placeholder={t(
                                'schedule.wizard.step1.productSearchPlaceholder',
                                '品目コードまたは名前を入力'
                              )}
                              value={searchQuery}
                              onChange={handleSearchQueryChange}
                              disabled={loadingCandidates}
                              className='nn-gcp-control'
                              style={{ flex: 1 }}
                            />
                            {searchQuery.trim() && (
                              <CloseIcon
                                sx={{ width: 20, height: 20 }}
                                className='absolute top-[20%] right-3 opacity-50 cursor-pointer hover:opacity-60'
                                onClick={handleClearSearch}
                              />
                            )}
                            <Popover
                              open={Boolean(anchorEl)}
                              anchorEl={anchorEl}
                              onClose={handleSearchClickAway}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left'
                              }}
                              disableAutoFocus
                              disableEnforceFocus
                            >
                              <Grow>
                                <Box
                                  sx={{
                                    width: ref.current?.offsetWidth,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: 100,
                                    maxHeight: 400,
                                    opacity: 'initial !important',
                                    visibility: 'initial !important',
                                    transform: 'initial !important'
                                  }}
                                >
                                  {loadingSearch ? (
                                    <div className='m-auto text-2xl'>
                                      <CircularProgress color='inherit' />
                                    </div>
                                  ) : hinmeiSearchResults.length > 0 ? (
                                    hinmeiSearchResults.map((item, index) => {
                                      const isSelected = hinmeiSelected.some(
                                        (s) => s.code === item.code
                                      )
                                      return (
                                        <HinmeiSearchItem
                                          key={item.code}
                                          item={item}
                                          isSelected={isSelected}
                                          isLast={
                                            index ===
                                            hinmeiSearchResults.length - 1
                                          }
                                          onSelect={(it) => {
                                            setHinmeiSelected((prev) => [
                                              ...prev,
                                              it
                                            ])
                                            setSearchQuery('')
                                            setAnchorEl(null)
                                          }}
                                        />
                                      )
                                    })
                                  ) : (
                                    <div className='m-auto text-2xl'>
                                      商品が見つかりません。
                                    </div>
                                  )}
                                </Box>
                              </Grow>
                            </Popover>
                          </Box>
                        </ClickAwayListener>
                      </div>

                      {hinmeiSelected.map((item, index) => (
                        <HinmeiSelectedItem
                          key={index}
                          item={item}
                          onRemove={removeHinmeiCode}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 40, textAlign: 'center' }}>
                  <button
                    onClick={handleGetCandidates}
                    disabled={loadingCandidates || !selectedSeller || !inventoryStartDate}
                    className='nn-btn nn-btn--primary nn-btn--lg text-2xl'
                  >
                    候補を出力
                  </button>
                </div>
              </div>
            )}

            {wizardStep === 2 && (
              <div style={{ padding: '8px' }}>
                <div style={step2BoxStyle}>
                  <div className='fs-18' style={step2HeaderStyle}>
                    <div style={{ flex: '1 1 40%', minWidth: 0, fontSize: 24 }}>
                      {t('schedule.wizard.step2.productSelection', '商品指定')}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.5em',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        flex: '1 1 40%',
                        minWidth: 0
                      }}
                    >
                      <button
                        type='button'
                        onClick={handleSelectAllHinmei}
                        className={`nn-btn ${
                          selectedHinmeiCodes.size === availableHinmei.length &&
                          availableHinmei.length > 0
                            ? 'nn-btn--primary'
                            : 'nn-btn--secondary'
                        } nn-btn--lg text-2xl`}
                      >
                        すべて選択
                      </button>
                      <button
                        type='button'
                        onClick={handleDeselectAllHinmei}
                        className={`nn-btn ${
                          selectedHinmeiCodes.size === 0
                            ? 'nn-btn--primary'
                            : 'nn-btn--secondary'
                        } nn-btn--lg text-2xl`}
                      >
                        すべて解除
                      </button>
                    </div>
                  </div>

                  <div style={step2GridContainerStyle}>
                    <div style={step2GridStyle}>
                      {availableHinmei.map((hinmei) => (
                        <HinmeiCard
                          key={hinmei.code}
                          hinmei={hinmei}
                          isSelected={selectedHinmeiCodes.has(hinmei.code)}
                          onToggle={toggleHinmeiSelection}
                          onOriginClick={handleOriginClick}
                          getOriginLabel={getOriginLabelForHinmei}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                <Popover
                  open={isOriginPopoverOpen}
                  anchorEl={originAnchorEl}
                  onClose={handleOriginClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: {
                        width: 'min(90vw, 400px)',
                        maxHeight: '40vh',
                        overflowY: 'auto',
                        p: 2,
                        mt: 1
                      }
                    }
                  }}
                >
                  {activeOriginHinmei && (
                    <Box>
                      {activeOriginHinmei.origins?.length > 0 ? (
                        <>
                          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <button
                              type='button'
                              onClick={handleOriginSelectAll(
                                activeOriginHinmei
                              )}
                              className='nn-btn nn-btn--primary nn-btn--lg px-4 py-2 text-2xl text-[#0969da] hover:bg-[#0969da1a] hover:text-[#0550ae]'
                            >
                              すべて
                            </button>
                            <button
                              type='button'
                              onClick={handleOriginDeselect(activeOriginHinmei)}
                              className='nn-btn nn-btn--secondary nn-btn--lg px-4 py-2 text-2xl text-[#57606a] hover:text-[#24292f] hover:bg-[#57606a1a]'
                            >
                              解除
                            </button>
                          </Box>
                          {activeOriginHinmei.origins.map((o) => {
                            const current =
                              selectedOriginCdsByHinmeiCode[
                                activeOriginHinmei.code
                              ] || []
                            const checked = current.includes(o.id)
                            return (
                              <OriginItem
                                key={`${activeOriginHinmei.code}-${o.cd}-${o.name}`}
                                o={o}
                                hinmei={activeOriginHinmei}
                                checked={checked}
                                onToggle={handleOriginToggle(
                                  activeOriginHinmei,
                                  o
                                )}
                              />
                            )
                          })}
                        </>
                      ) : (
                        <Box sx={{ opacity: 0.7, fontSize: 16 }}>産地なし</Box>
                      )}
                    </Box>
                  )}
                </Popover>

                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <button
                    onClick={handleProceedToAllocation}
                    disabled={
                      selectedHinmeiCodes.size === 0 || loadingAllocation
                    }
                    style={{
                      background: loadingAllocation ? '#f1f3f4' : '#4285f4',
                      color: loadingAllocation ? '#9aa0a6' : '#fff',
                      padding: '8px 24px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: loadingAllocation ? 'not-allowed' : 'pointer',
                      fontSize: '24px',
                      opacity:
                        selectedHinmeiCodes.size === 0 || loadingAllocation
                          ? 0.45
                          : 1
                    }}
                  >
                    {loadingAllocation ? '処理中...' : '荷割'}
                  </button>
                </div>
              </div>
            )}

            {wizardStep === 3 && (
              <div style={step3ContainerStyle}>
                <div style={{ position: 'relative', width: '100%', padding: '4px', minHeight: '400px' }}>
                  {loadingAllocation && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 255, 255, 0.75)',
                        backdropFilter: 'blur(3px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 100,
                        borderRadius: '8px',
                      }}
                    >
                      <div style={{ textAlign: 'center' }}>
                        <CircularProgress size={30} thickness={4} />
                        <div style={{ marginTop: '16px', fontSize: '18px', color: '#676869ff', fontWeight: 'bold' }}>
                          データ更新中です。
                        </div>
                      </div>
                    </div>
                  )}
                  {Object.keys(inventoryData).length > 0 ? (
                    <LeftInventoryPanel
                      inventoryData={inventoryData}
                      filteredInventoryData={filteredInventoryData}
                      shippingStartDate={shippingStartDate}
                      shippingEndDate={shippingEndDate}
                      setShippingStartDate={setShippingStartDate}
                      setShippingEndDate={setShippingEndDate}
                      shippingDateError={shippingDateError}
                      receivedStartDate={receivedStartDate}
                      receivedEndDate={receivedEndDate}
                      setReceivedStartDate={setReceivedStartDate}
                      setReceivedEndDate={setReceivedEndDate}
                      receivedDateError={receivedDateError}
                      activeHinmeiTab={activeHinmeiTab}
                      setActiveHinmeiTab={setActiveHinmeiTab}
                      selectedNyukaNumbers={selectedNyukaNumbers}
                      onSelectAll={handleSelectAllInventory}
                      onDeselectAll={handleDeselectAllInventory}
                      onToggleItem={handleToggleInventoryItem}
                      onOpenResult={handleOpenResult}
                      draftId={draftId}
                      hinmeiProductNames={hinmeiProductNames}
                    />
                  ) : inventoryReady ? (
                    <div
                      style={{
                        padding: '16px',
                        background: '#f8f9fa',
                        borderRadius: '4px',
                        textAlign: 'center'
                      }}
                    >
                      <div
                        className='fs-16'
                        style={{ color: '#5f6368', marginBottom: '4px' }}
                      >
                        在庫データがありません
                      </div>
                    </div>
                  ) : null}
                </div>

                {rightPanelVisible && (
                  <div
                    style={modalOverlayStyle}
                    onClick={() => setRightPanelVisible(false)}
                  >
                    <div
                      style={modalContentStyle}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div style={modalHeaderStyle}>
                        <h3 style={{ margin: 0, color: '#202124' }}>
                          配分結果
                        </h3>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => {
                              const params = new URLSearchParams({
                                selectedNyukaNumbers: encodeURIComponent(
                                  JSON.stringify(selectedNyukaNumbers)
                                ),
                                inventoryData: encodeURIComponent(
                                  JSON.stringify(inventoryData)
                                ),
                                activeHinmeiTab: activeHinmeiTab || ''
                              })
                              const url = `${window.location.origin}/allocation/schedule/right-panel?${params.toString()}`
                              const popup = window.open(
                                url,
                                '仮荷割ポップアップ',
                                `width=${Math.floor(window.screen.width * 0.9)},height=${Math.floor(window.screen.height * 0.8)},scrollbars=yes,resizable=yes,status=yes`
                              )
                              if (!popup)
                                addNotification(
                                  'error',
                                  'Popup was blocked by browser. Please allow popups for this site.'
                                )
                            }}
                            className='nn-btn nn-btn--primary nn-btn--lg text-2xl'
                          >
                            ポップアップで開く
                          </button>
                          <button
                            onClick={() => setRightPanelVisible(false)}
                            className='nn-btn nn-btn--secondary nn-btn--lg text-2xl'
                          >
                            閉じる
                          </button>
                        </div>
                      </div>
                      <RightNiwariPanel
                        selectedNyukaNumbers={selectedNyukaNumbers}
                        inventoryData={inventoryData}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const HinmeiSearchItem = memo(({ item, isSelected, isLast, onSelect }) => {
  const handleClick = useCallback(() => {
    if (!isSelected) onSelect(item)
  }, [isSelected, onSelect, item])
  return (
    <div
      style={{
        padding: '8px 12px',
        cursor: isSelected ? 'default' : 'pointer',
        borderBottom: isLast ? 'none' : '2px solid #9ca3af',
        fontSize: 24,
        backgroundColor: isSelected ? '#e0e0e0' : 'transparent'
      }}
      onClick={handleClick}
    >
      {item.name ? `${item.code} - ${item.name}` : item.code}
    </div>
  )
})

const HinmeiSelectedItem = memo(({ item, onRemove }) => {
  const handleRemove = useCallback(
    () => onRemove(item.code),
    [onRemove, item.code]
  )
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 8
      }}
    >
      <span
        style={{
          flex: 1,
          padding: '6px 12px',
          background: '#f3f4f6',
          borderRadius: '10px',
          fontSize: 17
        }}
      >
        {item.name ? `${item.code} - ${item.name}` : item.code}
      </span>
      <button
        onClick={handleRemove}
        className='nn-btn nn-btn--danger nn-btn--lg text-2xl'
      >
        ×
      </button>
    </div>
  )
})

const OriginItem = memo(({ o, hinmei, checked, onToggle }) => (
  <Box
    role='button'
    tabIndex={0}
    onClick={onToggle}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onToggle(e)
      }
    }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1,
      cursor: 'pointer',
      borderRadius: 1,
      '&:hover': { backgroundColor: 'action.hover' }
    }}
  >
    <input
      type='checkbox'
      checked={checked}
      onChange={() => {}}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => {
        e.stopPropagation()
        onToggle(e)
      }}
      style={{ width: 18, height: 18, cursor: 'pointer' }}
    />
    <span style={{ fontSize: 20 }}>
      {o.cd !== undefined && o.cd !== null && String(o.cd).length > 0
        ? `${o.cd}${o.name ? ` ${o.name}` : ''}`
        : o.name || ''}
    </span>
  </Box>
))

const pageLayoutStyle = {
  position: 'relative',
  width: '100%',
  maxWidth: '100vw',
  margin: 0,
  padding: '0 4px',
  boxSizing: 'border-box',
  zIndex: 1,
  overflowX: 'hidden',
  maxHeight: '90vh',
  overflowY: 'auto'
}
const wizardPanelStyle = {
  border: '0px solid #4285f4',
  borderRadius: '4px',
  padding: '8px',
  marginBottom: '4px',
  backgroundColor: '#ffffff',
  width: '100%',
  maxWidth: '100%',
  position: 'relative',
  zIndex: 1,
  boxSizing: 'border-box',
  overflowX: 'hidden'
}
const wizardHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
  minWidth: 0,
  marginBottom: '8px'
}
const wizardNavigationStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0
}
const step2BoxStyle = {
  borderRadius: '4px',
  border: '1px solid #dadce0',
  backgroundColor: '#ffffff',
  padding: '8px',
  overflow: 'hidden',
  position: 'relative'
}
const step2HeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  rowGap: '1%',
  gap: '2%',
  fontWeight: '500',
  marginBottom: '1.5%',
  color: '#202124',
  borderBottom: '1px solid #e8eaed',
  paddingBottom: '1.5%'
}
const step2GridContainerStyle = {
  minHeight: '20vh',
  height: '60%',
  overflowY: 'auto',
  border: '1px solid #e8eaed',
  borderRadius: '4px',
  padding: '2%'
}
const step2GridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '2%',
  maxWidth: '100%',
  margin: 0,
  overflow: 'visible',
  padding: '1%'
}
const step3ContainerStyle = {
  pointerEvents: 'auto',
  position: 'relative',
  zIndex: 20,
  width: '100%',
  maxWidth: '100%',
  overflowX: 'hidden'
}
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0,0,0,0.5)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}
const modalContentStyle = {
  backgroundColor: 'white',
  padding: '20px',
  borderRadius: '4px',
  maxWidth: '95%',
  maxHeight: '95%',
  width: '100%',
  height: 'auto',
  overflow: 'auto',
  position: 'relative',
  boxSizing: 'border-box'
}
const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
  borderBottom: '1px solid #dadce0',
  paddingBottom: '12px'
}

export default NewNiwari
